import React, { useEffect, useState } from 'react';
import Alert from 'components/ui/Alert';
import Label from 'components/v2/forms/Label';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import CargomaticDatePicker from 'components/v2/DatePicker';
import CargomaticSelect from 'components/v2/select';
function Asterisk() {
    return React.createElement("span", { className: "text-xs font-medium text-gray-500" }, "*");
}
const ContractForm = ({ data = {}, activeTermsAndConditions = [], saving, onValidSuccess, onValidFail, }) => {
    var _a, _b, _c;
    const [errors, setErrors] = useState({});
    const [formFields, setFormFields] = useState(Object.assign(Object.assign({ start_date: null, end_date: null, contractFile: null }, data), { terms_and_conditions: ((_b = (_a = data === null || data === void 0 ? void 0 : data.terms_and_conditions) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b._id) ||
            ((_c = activeTermsAndConditions === null || activeTermsAndConditions === void 0 ? void 0 : activeTermsAndConditions[0]) === null || _c === void 0 ? void 0 : _c._id) }));
    const { t } = useTranslation();
    const TODAY = moment().startOf('day');
    const isActiveContract = (data === null || data === void 0 ? void 0 : data.active) && (data === null || data === void 0 ? void 0 : data._id) && (data === null || data === void 0 ? void 0 : data.status) === 'active';
    const validateDates = (startDate, endDate) => {
        const dateErrors = {};
        let startDateMoment = moment.isMoment(startDate)
            ? startDate
            : moment(startDate);
        let endDateMoment = moment.isMoment(endDate) ? endDate : moment(endDate);
        if (moment(endDateMoment.format('YYYY-MM-DD')).isBefore(moment(startDateMoment.format('YYYY-MM-DD')))) {
            dateErrors.endDate = t('END_DATE_BEFORE_START_DATE');
        }
        if (!isActiveContract && startDateMoment.isBefore(TODAY)) {
            dateErrors.startDate = t('START_DATE_IN_PAST');
        }
        return dateErrors;
    };
    const validate = () => {
        let formErrors = {};
        if (!formFields.start_date) {
            formErrors.startDate = t('PROVIDE_START_DATE');
        }
        if (!formFields.end_date) {
            formErrors.endDate = t('PROVIDE_END_DATE');
        }
        if (formFields.start_date && formFields.end_date) {
            const dateErrors = validateDates(formFields.start_date, formFields.end_date);
            if (Object.keys(dateErrors).length) {
                return dateErrors;
            }
        }
        if (!formFields.terms_and_conditions) {
            formErrors.terms_and_conditions = t('TERMS_AND_CONDITIONS_MANDATORY');
        }
        return formErrors;
    };
    const renderErrors = () => {
        if (Object.keys(errors).length === 0) {
            return null;
        }
        return (React.createElement(Alert, { type: "error", title: t('FIX_FOLLOWING_ISSUES'), className: "mb-4" },
            React.createElement("ul", { "data-testid": "quickpay-error" }, Object.values(errors).map((error, i) => (React.createElement("li", { key: i }, error))))));
    };
    const handleChange = (name, value) => {
        setFormFields(Object.assign(Object.assign({}, formFields), { [name]: value }));
    };
    const handleDateChangeRaw = (field, value) => {
        if (!value.includes('_')) {
            handleChange(field, getDate(value));
        }
        else if (formFields[field]) {
            handleChange(field, '');
        }
    };
    const getData = () => {
        const { start_date, end_date, terms_and_conditions } = formFields;
        return {
            start_date,
            end_date,
            terms_and_conditions,
        };
    };
    const saveForm = async () => {
        const formErrors = validate();
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            onValidFail();
            return;
        }
        onValidSuccess(getData());
        setErrors({});
    };
    useEffect(() => {
        if (saving) {
            saveForm();
        }
    }, [saving]);
    const getDate = (value) => {
        if (!value) {
            return '';
        }
        return moment(value);
    };
    return (React.createElement(React.Fragment, null,
        renderErrors(),
        React.createElement("div", { "data-testid": "contract-form", className: "flex flex-col sm:flex-row justify-start sm:items-center sm:space-x-8 space-y-4 sm:space-y-0 " },
            React.createElement("div", null,
                React.createElement(Label, { htmlFor: "variableStartDate" },
                    t('START_DATE'),
                    " ",
                    React.createElement(Asterisk, null)),
                React.createElement(CargomaticDatePicker, { disabled: isActiveContract, className: "mt-1", selected: formFields === null || formFields === void 0 ? void 0 : formFields.start_date, onChange: (val) => handleChange('start_date', val), onChangeRaw: ({ target: { value } }) => handleDateChangeRaw('start_date', value), minDate: getDate(new Date()) })),
            React.createElement("div", null,
                React.createElement(Label, { htmlFor: "variableEndDate" },
                    t('END_DATE'),
                    " ",
                    React.createElement(Asterisk, null)),
                React.createElement(CargomaticDatePicker, { className: "mt-1", selected: formFields === null || formFields === void 0 ? void 0 : formFields.end_date, onChange: (val) => handleChange('end_date', val), onChangeRaw: ({ target: { value } }) => handleDateChangeRaw('end_date', value), minDate: (formFields === null || formFields === void 0 ? void 0 : formFields.start_date)
                        ? getDate(formFields.start_date)
                        : getDate(new Date()) })),
            React.createElement("div", null,
                React.createElement(CargomaticSelect, { showCaption: true, label: t('TERMS_AND_CONDITIONS_ASTERISK'), selected: formFields.terms_and_conditions, items: activeTermsAndConditions, onSelect: (item) => {
                        handleChange('terms_and_conditions', item._id);
                    }, applyDefaultMargins: false, valueKey: "_id", textKey: "displayText", disabled: !(activeTermsAndConditions === null || activeTermsAndConditions === void 0 ? void 0 : activeTermsAndConditions.length) })))));
};
export default ContractForm;
